<!-- SeverityScale.vue - first "good example or problem", then "what happens here" chips that suggest the level.
     v-model: null (unset) | 0 praise | 1 nuisance (annoying) | 2 danger. The stored values stay the same as before. -->
<template>
  <div class="severity-scale fade-in">
    <div class="kinds" role="radiogroup" :aria-label="$t('report.severityScale.kindQuestion')">
      <button
        v-for="k in kinds"
        :key="k.key"
        type="button"
        role="radio"
        class="kind"
        :class="{ on: kind === k.key }"
        :aria-checked="kind === k.key ? 'true' : 'false'"
        @click="pickKind(k.key)"
      >
        <span class="dot" :class="k.dot" aria-hidden="true"></span>
        <strong>{{ k.title }}</strong>
        <span class="sub">{{ k.sub }}</span>
      </button>
    </div>

    <template v-if="kind === 'problem'">
      <fieldset class="happens">
        <legend>{{ $t('report.severityScale.whatHappens') }}</legend>
        <p class="help">{{ $t('report.severityScale.whatHappensHelp') }}</p>
        <div class="chips">
          <button
            v-for="c in chips"
            :key="c.key"
            type="button"
            class="chip"
            :class="{ on: picked.includes(c.key) }"
            :aria-pressed="picked.includes(c.key) ? 'true' : 'false'"
            @click="toggleChip(c.key)"
          >
            <i v-if="c.ai" class="fas fa-wand-magic-sparkles ai-mark" aria-hidden="true"></i>
            {{ c.ai ? c.text : $t('report.severityScale.chips.' + c.key) }}
          </button>
        </div>
        <p v-if="generatedChips.length" class="ai-chips-note" data-ai-generated="true">{{ $t('report.severityScale.aiChipsNote') }}</p>
      </fieldset>

      <div class="levels" role="radiogroup" :aria-label="$t('report.severityScale.levelTitle')">
        <p class="levels-title">{{ $t('report.severityScale.levelTitle') }}</p>
        <div class="level-row">
          <button
            v-for="l in levels"
            :key="l.value"
            type="button"
            role="radio"
            class="level"
            :class="[l.color, { on: modelValue === l.value }]"
            :aria-checked="modelValue === l.value ? 'true' : 'false'"
            @click="pickLevel(l.value)"
          >
            <span class="dot" :class="l.color" aria-hidden="true"></span>
            <strong>{{ l.label }}</strong>
            <span class="sub">{{ l.example }}</span>
          </button>
        </div>
      </div>
    </template>

    <p class="hint" role="status">
      <template v-if="kind === null">{{ $t('report.severityScale.unset') }}</template>
      <template v-else-if="kind === 'problem' && modelValue === null">{{ $t('report.severityScale.pickProblem') }}</template>
      <template v-else-if="kind === 'problem' && !manual && picked.length">
        {{ $t('report.severityScale.basedOn', { level: currentLabel, n: picked.length }, picked.length) }}
      </template>
    </p>
  </div>
</template>

<script>
// Each answer points to a level: 1 = annoying (nuisance), 2 = dangerous
const CHIPS = [
  { key: 'slow', level: 1 },
  { key: 'blocked', level: 1 },
  { key: 'bumpy', level: 1 },
  { key: 'swerve', level: 2 },
  { key: 'fell', level: 2 },
  { key: 'unsafe', level: 2 },
];

// Phrases derived from detected labels when the photo analysis gave none
const LABEL_PHRASES = [
  { key: 'vehicleInWay', level: 1, labels: ['parked car', 'car', 'van', 'truck', 'delivery vehicle', 'motorcycle', 'e-scooter'] },
  { key: 'blockedLane', level: 1, labels: ['blocked bike lane', 'obstruction', 'barrier', 'construction'] },
  { key: 'damagedSurface', level: 1, labels: ['pothole', 'crack', 'cobblestones', 'gravel', 'debris', 'glass'] },
  { key: 'slippery', level: 2, labels: ['ice', 'snow', 'puddle', 'leaves', 'tram tracks'] },
  { key: 'noSpace', level: 2, labels: ['narrow lane', 'missing bike lane', 'shared path'] },
];

export default {
  props: {
    modelValue: { type: Number, default: null },
    // short phrases made from the photo: [{ text, level }]
    generated: { type: Array, default: () => [] },
    // detected labels [{ label, amount }]: used for suggestions when the photo analysis gave no phrases
    labels: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'details'],
  data() {
    return {
      kind: this.modelValue === 0 ? 'good' : this.modelValue === null ? null : 'problem',
      picked: [],
      manual: this.modelValue === 1 || this.modelValue === 2, // an existing choice is not overwritten by the chips
    };
  },
  computed: {
    generatedChips() {
      const fromAi = (this.generated || []).map((g) => ({ text: g.text, level: g.level === 2 ? 2 : 1 }));
      // no phrases from the analysis (e.g. it was unavailable): derive a few from what was detected in the photo
      const found = new Set((this.labels || []).map((l) => l.label));
      const fromLabels = fromAi.length ? [] : LABEL_PHRASES.filter((p) => p.labels.some((l) => found.has(l))).map((p) => ({ text: this.$t('report.severityScale.phrases.' + p.key), level: p.level }));
      return [...fromAi, ...fromLabels].slice(0, 3).map((g) => ({ key: 'ai:' + g.text, text: g.text, level: g.level, ai: true }));
    },
    // photo-based phrases first, then the generic answers that fit every situation
    chips() { return [...this.generatedChips, ...CHIPS]; },
    kinds() {
      return [
        { key: 'good', dot: 'low', title: this.$t('report.severityScale.kindGood'), sub: this.$t('report.severityScale.kindGoodSub') },
        { key: 'problem', dot: 'high', title: this.$t('report.severityScale.kindProblem'), sub: this.$t('report.severityScale.kindProblemSub') },
      ];
    },
    levels() {
      return [
        { value: 1, color: 'medium', label: this.$t('report.severityScale.annoying'), example: this.$t('report.severityScale.annoyingExample') },
        { value: 2, color: 'high', label: this.$t('report.severityScale.dangerous'), example: this.$t('report.severityScale.dangerousExample') },
      ];
    },
    suggested() {
      if (!this.picked.length) return null;
      return this.picked.some((k) => { const c = this.chips.find((x) => x.key === k); return c && c.level === 2; }) ? 2 : 1;
    },
    currentLabel() {
      const l = this.levels.find((x) => x.value === this.modelValue);
      return l ? l.label : '';
    },
  },
  watch: {
    modelValue(v) {
      if (v === 0) this.kind = 'good';
      else if (v === 1 || v === 2) this.kind = 'problem';
    },
  },
  methods: {
    emit(v) {
      if (v !== this.modelValue) this.$emit('update:modelValue', v);
    },
    // Tell the form how the rating came about; it is stored in the report's free-form details
    emitDetails() {
      this.$emit('details', {
        kind: this.kind,
        answers: this.kind === 'problem' ? [...this.picked] : [],
        suggested: this.kind === 'problem' ? this.suggested : null,
        manual: this.kind === 'problem' ? this.manual : false,
      });
    },
    pickKind(key) {
      this.kind = key;
      this.$nextTick(() => this.emitDetails());
      if (key === 'good') this.emit(0);
      else this.emit(this.modelValue === 1 || this.modelValue === 2 ? this.modelValue : this.suggested);
    },
    toggleChip(key) {
      const i = this.picked.indexOf(key);
      if (i >= 0) this.picked.splice(i, 1);
      else this.picked.push(key);
      if (!this.manual) this.emit(this.suggested);
      this.emitDetails();
    },
    pickLevel(v) {
      this.manual = true;
      this.emit(v);
      this.emitDetails();
    },
  },
};
</script>

<style scoped>
.severity-scale { display: flex; flex-direction: column; gap: 16px; width: 100%; text-align: left; }
.kinds, .level-row { display: flex; gap: 12px; }
.kind, .level {
  flex: 1 1 0; display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 14px; min-height: 44px;
  background: var(--color-surface); color: var(--color-text); border: 1px solid var(--color-border-input); border-radius: var(--radius-lg);
  font: inherit; text-align: left; cursor: pointer;
}
.kind.on, .level.on { border: 3px solid var(--color-focus); padding: 12px; }
.kind strong, .level strong { font-size: 16px; font-weight: 600; }
.sub { font-size: 12px; color: var(--color-secondary); line-height: 1.35; }
.dot { flex: none; width: 12px; height: 12px; border-radius: 50%; }
.dot.low { background: var(--color-severity-low); }
.dot.medium { background: var(--color-severity-medium); }
.dot.high { background: var(--color-severity-high); }
.happens { border: 0; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.happens legend { padding: 0; font-size: 16px; font-weight: 600; margin-bottom: 4px; }
.help { margin: 0; font-size: 14px; color: var(--color-secondary); }
.ai-mark { font-size: 11px; margin-inline-end: 4px; color: var(--color-primary-text); }
.ai-chips-note { margin: 6px 0 0; font-size: 12px; color: var(--color-secondary); }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.chip {
  min-height: 44px; padding: 8px 14px; font: inherit; font-size: 14px; color: var(--color-text); background: var(--color-surface);
  border: 1px solid var(--color-border-input); border-radius: var(--radius-pill); cursor: pointer;
}
.chip.on { background: var(--color-primary); color: var(--color-on-primary); border-color: var(--color-primary-dark); font-weight: 600; }
.levels-title { margin: 0 0 8px; font-size: 16px; font-weight: 600; }
.hint { min-height: 22px; margin: 0; font-size: 14px; color: var(--color-secondary); text-align: center; }
.kind:focus-visible, .level:focus-visible, .chip:focus-visible { outline: 3px solid var(--color-focus); outline-offset: 2px; }
</style>
